import { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Clock, Plus, Check, Calendar, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VOLUNTEER_ROLES = [
  "Volunteer Coordinator",
  "Family Engagement Support",
  "Bookshelf Delivery Driver",
  "Bookshelf Assembly Team",
  "Bookshelf Artisan",
  "Event Support",
  "Administrative Support",
  "Other"
];

export default function HourTracking() {
  const [hours, setHours] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedHour, setSelectedHour] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [hourForm, setHourForm] = useState({
    volunteer_id: '',
    event_id: '',
    hours: '',
    date: '',
    description: '',
    role: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [hoursRes, volunteersRes, eventsRes] = await Promise.all([
        fetch(`${API}/hours`, { credentials: 'include' }),
        fetch(`${API}/volunteers`, { credentials: 'include' }),
        fetch(`${API}/events`, { credentials: 'include' })
      ]);

      if (hoursRes.ok) setHours(await hoursRes.json());
      if (volunteersRes.ok) setVolunteers(await volunteersRes.json());
      if (eventsRes.ok) setEvents(await eventsRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await fetch(`${API}/hours`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...hourForm,
          hours: parseFloat(hourForm.hours)
        })
      });

      if (response.ok) {
        toast.success('Hours logged successfully');
        fetchData();
        setShowDialog(false);
        setHourForm({
          volunteer_id: '',
          event_id: '',
          hours: '',
          date: '',
          description: '',
          role: ''
        });
      } else {
        toast.error('Failed to log hours');
      }
    } catch (error) {
      console.error('Error logging hours:', error);
      toast.error('Failed to log hours');
    } finally {
      setSubmitting(false);
    }
  };

  const approveHours = async (hourId) => {
    try {
      const response = await fetch(`${API}/hours/${hourId}/approve`, {
        method: 'PUT',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Hours approved');
        fetchData();
      } else {
        toast.error('Failed to approve hours');
      }
    } catch (error) {
      console.error('Error approving hours:', error);
      toast.error('Failed to approve hours');
    }
  };

  const openDeleteDialog = (hour) => {
    setSelectedHour(hour);
    setShowDeleteDialog(true);
  };

  const handleDeleteHour = async () => {
    if (!selectedHour) return;
    
    try {
      const response = await fetch(`${API}/hours/${selectedHour.hour_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Hours record deleted');
        setShowDeleteDialog(false);
        setSelectedHour(null);
        fetchData();
      } else {
        toast.error('Failed to delete hours record');
      }
    } catch (error) {
      console.error('Error deleting hours:', error);
      toast.error('Failed to delete hours record');
    }
  };

  const getVolunteerName = (volunteerId) => {
    const volunteer = volunteers.find(v => v.volunteer_id === volunteerId);
    return volunteer ? `${volunteer.first_name} ${volunteer.last_name}` : 'Unknown';
  };

  const getEventTitle = (eventId) => {
    const event = events.find(e => e.event_id === eventId);
    return event ? event.title : 'General';
  };

  const totalHours = hours.reduce((sum, h) => sum + (h.hours || 0), 0);
  const pendingHours = hours.filter(h => !h.approved).reduce((sum, h) => sum + (h.hours || 0), 0);

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="hour-tracking">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#1a3a5c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Hour Tracking
            </h1>
            <p className="text-slate-500 mt-1">Log and manage volunteer hours</p>
          </div>
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button className="bg-[#1a3a5c] hover:bg-[#1a3a5c]/90" data-testid="log-hours-btn">
                <Plus className="w-4 h-4 mr-2" />
                Log Hours
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Log Volunteer Hours</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="volunteer">Volunteer *</Label>
                  <Select 
                    value={hourForm.volunteer_id} 
                    onValueChange={(value) => setHourForm(prev => ({ ...prev, volunteer_id: value }))}
                  >
                    <SelectTrigger data-testid="hour-volunteer-select">
                      <SelectValue placeholder="Select volunteer" />
                    </SelectTrigger>
                    <SelectContent>
                      {volunteers.map((volunteer) => (
                        <SelectItem key={volunteer.volunteer_id} value={volunteer.volunteer_id}>
                          {volunteer.first_name} {volunteer.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="event">Event (optional)</Label>
                  <Select 
                    value={hourForm.event_id} 
                    onValueChange={(value) => setHourForm(prev => ({ ...prev, event_id: value }))}
                  >
                    <SelectTrigger data-testid="hour-event-select">
                      <SelectValue placeholder="Select event" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">General (No event)</SelectItem>
                      {events.map((event) => (
                        <SelectItem key={event.event_id} value={event.event_id}>
                          {event.title} ({event.date})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="hours">Hours *</Label>
                    <Input
                      id="hours"
                      type="number"
                      step="0.5"
                      min="0.5"
                      value={hourForm.hours}
                      onChange={(e) => setHourForm(prev => ({ ...prev, hours: e.target.value }))}
                      required
                      data-testid="hour-amount-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="date">Date *</Label>
                    <Input
                      id="date"
                      type="date"
                      value={hourForm.date}
                      onChange={(e) => setHourForm(prev => ({ ...prev, date: e.target.value }))}
                      required
                      data-testid="hour-date-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Select 
                    value={hourForm.role} 
                    onValueChange={(value) => setHourForm(prev => ({ ...prev, role: value }))}
                  >
                    <SelectTrigger data-testid="hour-role-select">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      {VOLUNTEER_ROLES.map((role) => (
                        <SelectItem key={role} value={role}>{role}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={hourForm.description}
                    onChange={(e) => setHourForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="What did the volunteer do?"
                    data-testid="hour-description-input"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting} className="bg-[#1a3a5c] hover:bg-[#1a3a5c]/90" data-testid="save-hours-btn">
                    {submitting ? 'Saving...' : 'Log Hours'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-3 gap-4">
          <Card className="border-0 shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#5cc8e8]/10 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-[#5cc8e8]" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Hours</p>
                  <p className="text-2xl font-bold text-[#1a3a5c]">{totalHours.toFixed(1)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Pending Approval</p>
                  <p className="text-2xl font-bold text-[#1a3a5c]">{pendingHours.toFixed(1)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Check className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Approved Hours</p>
                  <p className="text-2xl font-bold text-[#1a3a5c]">{(totalHours - pendingHours).toFixed(1)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Hours Table */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle>Hour Logs</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-[#5cc8e8] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : hours.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Volunteer</TableHead>
                      <TableHead className="font-semibold">Event</TableHead>
                      <TableHead className="font-semibold">Date</TableHead>
                      <TableHead className="font-semibold">Hours</TableHead>
                      <TableHead className="font-semibold">Role</TableHead>
                      <TableHead className="font-semibold">Status</TableHead>
                      <TableHead className="font-semibold">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {hours.map((hour) => (
                      <TableRow key={hour.hour_id} className="table-row-hover">
                        <TableCell className="font-medium text-[#1a3a5c]">
                          {getVolunteerName(hour.volunteer_id)}
                        </TableCell>
                        <TableCell className="text-slate-600">
                          {getEventTitle(hour.event_id)}
                        </TableCell>
                        <TableCell className="text-slate-600">{hour.date}</TableCell>
                        <TableCell className="font-medium text-[#1a3a5c]">{hour.hours}</TableCell>
                        <TableCell className="text-slate-600">{hour.role || '-'}</TableCell>
                        <TableCell>
                          <span className={`status-badge ${hour.approved ? 'status-approved' : 'status-pending'}`}>
                            {hour.approved ? 'Approved' : 'Pending'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {!hour.approved && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => approveHours(hour.hour_id)}
                                data-testid={`approve-hour-${hour.hour_id}`}
                              >
                                <Check className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => openDeleteDialog(hour)}
                              data-testid={`delete-hour-${hour.hour_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                <Clock className="w-12 h-12 mb-4 text-slate-300" />
                <p>No hours logged yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Delete Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Hours Record</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-slate-600">
                Are you sure you want to delete this hours record for{' '}
                <strong>{selectedHour ? getVolunteerName(selectedHour.volunteer_id) : ''}</strong>?
              </p>
              <p className="text-sm text-slate-500">
                {selectedHour?.hours} hours on {selectedHour?.date}
              </p>
              <p className="text-sm text-red-600">
                This action cannot be undone.
              </p>
              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowDeleteDialog(false)} 
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleDeleteHour}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  data-testid="confirm-delete-hour-btn"
                >
                  Delete Record
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
